// /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/Showcase.tsx

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Button from "./ui/Button.tsx";

// Item-Definition - Struktur jedes Showcase-Eintrags
export interface ShowcaseItem {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  link: string;
}

// Props der Komponente
interface ShowcaseProps {
  items: ShowcaseItem[];
  heading: {
    subtitle?: string;
    title: string;
    explore: string;
    viewAllText?: string;
    viewAllLink?: string;
  };
  learnMoreText: string;
  className?: string;
  onItemClick?: (itemId: number) => void;
}

const Showcase: React.FC<ShowcaseProps> = ({
  items,
  heading,
  learnMoreText,
  className = "",
  onItemClick,
}) => {
  const [activeItem, setActiveItem] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);
  const navigate = useNavigate();
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Höhe des aktiven Items messen
  useEffect(() => {
    const measureHeight = () => {
      const activeRef = itemRefs.current[activeItem];
      if (activeRef) {
        const height = activeRef.getBoundingClientRect().height;
        setContainerHeight(height);
      }
    };

    measureHeight();
    const timer = setTimeout(measureHeight, 100);
    window.addEventListener("resize", measureHeight);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measureHeight);
    };
  }, [activeItem, items]);

  // Handler beim Klick auf ein Item
  const handleItemClick = (index: number): void => {
    if (index === activeItem) return;

    if (onItemClick) {
      onItemClick(items[index].id);
    }

    setActiveItem(index);
  };

  return (
    <div className={`w-full bg-black text-white ${className}`}>
      {/* Intro mit Titel & "View all"-Link */}
      {heading && (
        <div className="max-w-[1510px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-5 ml:mb-15 lg:mb-20">
            {heading.subtitle && (
              <div className="text-white text-xl md:text-1xl lg:text-2xl mb-2">
                {heading.subtitle}
              </div>
            )}

            {/* Flexbox Container für Titel und Link nebeneinander */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="text-4xl md:text-5xl lg:text-7xl font-bold mb-0">
                {heading.explore}
              </div>

              {/* Link rechts neben dem Titel */}
              {heading.viewAllText && heading.viewAllLink && (
                <div className="flex justify-start lg:justify-end">
                  <Button size="M" variant="bordered" to={heading.viewAllLink}>
                    {heading.viewAllText}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hauptinhalt: Grid mit Liste links und Beschreibung rechts */}
      <div className="max-w-[1510px] mx-auto mb-[150px] md:mb-[150px] lg:mb-[300px]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-0">
          {/* Linke Seite: Liste der Items */}
          <div className="order-1 lg:order-1">
            <div className="space-y-1 md:space-y-2 lg:space-y-3 px-4">
              {items.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(index)}
                  type="button"
                  className="w-full text-left py-2 md:py-3 lg:py-5 px-1 md:px-2 lg:px-4 transition-all duration-200 relative group"
                  aria-pressed={index === activeItem}
                >
                  <div
                    className={`text-2xl md:text-3xl lg:text-[40px] font-bold transition-colors duration-200 justify-center ${
                      index === activeItem
                        ? "text-white"
                        : "text-white/30 group-hover:text-white"
                    }`}
                  >
                    {item.title}
                  </div>

                  {/* Unterstrich über gesamte Button-Breite */}
                  <div
                    className={`absolute w-full h-[1px] transition-transform duration-200 origin-left ${
                      index === activeItem
                        ? "bottom-[10px] scale-x-100 bg-white"
                        : "bottom-[0px] scale-x-0 group-hover:scale-x-100"
                    }`}
                  ></div>
                </button>
              ))}
            </div>
          </div>

          {/* Rechte Seite: Beschreibung als Carousel */}
          <div className="order-2 md:order-2 lg:order-2 px-1 md:px-2 lg:px-4">
            <div
              className="relative overflow-hidden transition-all duration-500 ease-in-out"
              style={{
                height:
                  containerHeight !== null ? `${containerHeight}px` : "auto",
              }}
            >
              {items.map((item, index) => (
                <div
                  key={item.id}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  className="absolute top-0 w-full px-4 py-4 md:py-2 lg:py-2 transition-transform duration-1000 ease-in-out"
                  style={{
                    transform: `translateX(${(index - activeItem) * 100}%)`,
                  }}
                >
                  <p className="text-white text-2xl md:text-3xl lg:text-[40px] font-bold !leading-[1.2]">
                    {item.subtitle}
                  </p>
                  <p className="text-white text-xl md:text-1xl lg:text-2xl my-3 md:my-7 lg:my-8 font-semibold">
                    {item.description}
                  </p>
                  {/* Button nur anzeigen wenn Link vorhanden */}
                  {item.link && item.link.trim() !== "" && (
                    <Button to={item.link} variant="bordered" size="M">
                      {learnMoreText}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Showcase;
